import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { cn } from '@/lib/utils';

type ImageData = {
  src: string;
  alt: string;
  title: string;
  caption: string;
};

const CarouselWithBlur = ({
  images,
  autoplayPlugin,
}: {
  images: ImageData[];
  autoplayPlugin: React.MutableRefObject<ReturnType<typeof Autoplay>>;
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on('select', onSelect);
    return () => { api.off('select', onSelect); };
  }, [api, onSelect]);

  return (
    <Carousel
      opts={{ align: 'center', loop: true }}
      plugins={[autoplayPlugin.current]}
      setApi={setApi}
      className="w-full"
    >
      <CarouselContent>
        {images.map((img, i) => {
          const isActive = i === current;
          return (
            <CarouselItem key={i} className="md:basis-1/2 lg:basis-1/2">
              <figure
                className={cn(
                  "flex flex-col items-center px-2 transition-all duration-500",
                  isActive ? "opacity-100 scale-100" : "opacity-40 scale-95 blur-[2px]"
                )}
              >
                <div className="overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 bg-card border border-border">
                  <img
                    src={img.src}
                    alt={img.alt}
                    title={img.title}
                    width={600}
                    height={600}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto object-cover aspect-square"
                  />
                </div>
                <figcaption className="mt-3 text-sm text-muted-foreground text-center max-w-xs leading-relaxed">
                  {img.caption}
                </figcaption>
              </figure>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselPrevious className="hidden sm:flex -left-4 lg:-left-12" />
      <CarouselNext className="hidden sm:flex -right-4 lg:-right-12" />
    </Carousel>
  );
};

const imagesFr = [
  {
    src: '/images/formation-ia-juridique-avocat-prerequis-chatgpt.webp',
    alt: "Formation IA juridique pour avocats : 6 prérequis essentiels pour maîtriser ChatGPT, Claude et Gemini dans votre pratique du droit",
    title: "6 prérequis - Formation IA générative pour avocats et juristes | Gaius",
    caption: "Les 6 prérequis indispensables pour utiliser l'intelligence artificielle générative efficacement dans votre cabinet d'avocats",
  },
  {
    src: '/images/chatgpt-avocat-prerequis-prompts-rag-automatisation.webp',
    alt: "Les 6 étapes pour maîtriser l'IA juridique : cas d'usage, chatbot adapté, prompt engineering, RAG, formatage et automatisation pour avocats",
    title: "Formation avocat IA : choisir le bon outil (ChatGPT, Claude, Harvey) et maîtriser le prompt engineering juridique",
    caption: "Méthodologie complète : du choix du chatbot IA (ChatGPT, Claude, Gemini) à la création de GPT personnalisés pour avocats",
  },
  {
    src: '/images/formation-debutant-chatgpt-claude-avocat-securite.webp',
    alt: "Formation IA débutant pour avocats : apprendre à utiliser ChatGPT, Gemini et Claude en toute sécurité et conformité déontologique CNB",
    title: "Formation débutant IA juridique : maîtriser ChatGPT pour avocats avec sécurité RGPD et conformité CNB",
    caption: "Programme débutant : prise en main de ChatGPT, Claude et Gemini pour juristes avec focus sécurité et secret professionnel",
  },
  {
    src: '/images/programme-formation-ia-avocat-prompt-engineering-12-etapes.webp',
    alt: "Programme formation IA avocat débutant : introduction IA générative, prise en main ChatGPT/Gemini, sécurité RGPD, prompt engineering 12 étapes, formatage réponses",
    title: "Formation CPF IA avocat : programme complet ChatGPT - Prompt engineering juridique en 12 étapes | Éligible FIF PL OPCO",
    caption: "Parcours de formation IA pour avocats éligible CPF et FIF PL : du prompt engineering à la mise en forme des réponses juridiques",
  },
  {
    src: '/images/formation-avancee-ia-juridique-automatisation-cabinet-avocat.webp',
    alt: "Formation IA avancée pour avocats : maîtriser cas d'usage complexes, automatiser tâches chronophages cabinet, optimiser productivité avec ChatGPT et Claude",
    title: "Formation avancée IA juridique : automatisation cabinet avocat, cas complexes, GPT personnalisés | Gaius - Éligible OPCO FCO",
    caption: "Niveau avancé : automatisation complète du cabinet d'avocats avec IA - Due diligence, recherche jurisprudentielle, rédaction actes",
  },
  {
    src: '/images/cas-usage-ia-juridique-verification-automatisation-avocat.webp',
    alt: "6 cas d'usage concrets de l'IA pour avocats : recherche juridique, vérification arguments, automatisation actes, prospection et GPT juridique personnalisé",
    title: "IA juridique : cas d'usage pratiques pour avocats - Vérification hallucinations, automatisation, prospection | Gaius Formation",
    caption: "Applications concrètes de ChatGPT et Claude en cabinet : de la vérification d'argumentaires à l'automatisation de la prospection juridique",
  },
];

const imagesEn = [
  {
    src: '/images/legal-ai-training-6-prerequisites-chatbot-generative-ai.webp',
    alt: "Legal AI training for lawyers: 6 must-have prerequisites to effectively use ChatGPT, Claude and Gemini chatbots based on generative AI",
    title: "6 Must-Have Prerequisites - Generative AI Training for Lawyers | Gaius",
    caption: "The 6 essential prerequisites to effectively use generative AI chatbots in your law practice",
  },
  {
    src: '/images/legal-ai-training-6-steps-prompt-rag-automation.webp',
    alt: "6 steps to master legal AI: choose a use case, pick the right chatbot, learn prompting, RAG, formatting and automation for lawyers",
    title: "Legal AI Training: Choose the Right Tool (ChatGPT, Claude, Harvey) and Master Legal Prompt Engineering",
    caption: "Complete methodology: from choosing the right AI chatbot (ChatGPT, Claude, Gemini) to mastering prompt engineering for lawyers",
  },
  {
    src: '/images/legal-ai-training-beginner-chatgpt-gemini-claude-security.webp',
    alt: "Beginner legal AI training for lawyers: get familiar with ChatGPT, Gemini and Claude and use them securely with professional confidentiality",
    title: "Beginner Legal AI Training: Master ChatGPT for Lawyers with Security and Compliance Focus",
    caption: "Beginner program: getting started with ChatGPT, Claude and Gemini for lawyers with security and confidentiality focus",
  },
  {
    src: '/images/legal-ai-training-beginner-program-prompt-engineering-12-steps.webp',
    alt: "Beginner legal AI training program: introduction to generative AI, ChatGPT/Gemini onboarding, security, prompt engineering in 12 steps, response formatting",
    title: "Legal AI Training Program: Complete ChatGPT Course - Prompt Engineering in 12 Steps | Gaius",
    caption: "AI training pathway for lawyers: from prompt engineering basics to formatting legal AI responses in 12 steps",
  },
  {
    src: '/images/legal-ai-training-advanced-automation-law-firm.webp',
    alt: "Advanced legal AI training for lawyers: master complex use cases and automate time-consuming tasks in your law firm with ChatGPT and Claude",
    title: "Advanced Legal AI Training: Law Firm Automation, Complex Use Cases, Custom GPTs | Gaius",
    caption: "Advanced level: complete law firm automation with AI - due diligence, case law research, document drafting",
  },
  {
    src: '/images/legal-ai-training-advanced-use-cases-verification-automation.webp',
    alt: "6 practical AI use cases for lawyers: legal research, argument verification, document automation, prospecting and custom legal GPT",
    title: "Legal AI: Practical Use Cases for Lawyers - Hallucination Verification, Automation, Prospecting | Gaius Training",
    caption: "Practical applications of ChatGPT and Claude in law firms: from argument verification to prospecting automation",
  },
];

export const AILevelSection = () => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const images = isFr ? imagesFr : imagesEn;

  const autoplayPlugin = React.useRef(
    Autoplay({ delay: 4000, stopOnInteraction: true })
  );

  return (
    <section className="py-20 section-gradient-violet">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl lg:text-4xl font-bold text-foreground text-center mb-6">
          {isFr
            ? "Formation IA Avocat : Quel est votre niveau ?"
            : "Legal AI Training: What's Your Level?"}
        </h2>

        <p className="text-lg text-muted-foreground max-w-4xl mx-auto text-center mb-14">
          {isFr ? (
            <>
              Gaius propose des formations IA juridique <b>bientôt</b> éligibles CPF, FIF PL et OPCO pour avocats et juristes. Nos programmes couvrent <b>notamment</b> l'utilisation sécurisée de ChatGPT, Claude et Gemini dans votre pratique du droit, avec un focus déontologie CNB et vérification des hallucinations IA.
              <br />Formations débutant et avancées disponibles, certifiées Qualiopi, adaptées aux besoins des cabinets d'avocats et directions juridiques.
            </>
          ) : (
            <>
              Gaius offers expert legal AI training for lawyers and legal professionals. Our programs cover the secure use of ChatGPT, Claude and Gemini in your legal practice, with a focus on ethics, compliance and AI hallucination verification.
              <br />Beginner and advanced programs available, Qualiopi-certified, tailored to the needs of law firms and legal departments.
            </>
          )}
        </p>

        <CarouselWithBlur images={images} autoplayPlugin={autoplayPlugin} />
      </div>
    </section>
  );
};
